import { useMemo } from "react";
import { ComposedChart, Line, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid, Area } from "recharts";
import { fmtDate, fmtShort, usd } from "./common";

const GRID = "#1E2738";
const AX = { stroke: "#64748B", fontSize: 10, fontFamily: "monospace" };
const TT = { contentStyle: { background: "#121724", border: "1px solid #2A364F", borderRadius: 6, fontSize: 11, fontFamily: "monospace" }, labelFormatter: (v) => fmtDate(v) };

const Marker = ({ cx, cy, type }) => {
  if (cx == null || cy == null) return null;
  if (type === "long") return <path d={`M${cx},${cy - 7} L${cx - 6},${cy + 4} L${cx + 6},${cy + 4} Z`} fill="#10B981" stroke="#0B0E17" strokeWidth={1} />;
  if (type === "short") return <path d={`M${cx},${cy + 7} L${cx - 6},${cy - 4} L${cx + 6},${cy - 4} Z`} fill="#EF4444" stroke="#0B0E17" strokeWidth={1} />;
  return <circle cx={cx} cy={cy} r={4} fill="#94A3B8" stroke="#0B0E17" strokeWidth={1} />;
};

export const PriceChart = ({ series, markers }) => {
  const data = useMemo(() => {
    const byT = new Map(series.map((s) => [s.t, { ...s }]));
    markers.forEach((m) => { const row = byT.get(m.t); if (row) row[`m_${m.type}`] = m.price; });
    return Array.from(byT.values());
  }, [series, markers]);
  const dom = useMemo(() => { const v = series.flatMap((s) => [s.l, s.h]); return [Math.min(...v) * 0.995, Math.max(...v) * 1.005]; }, [series]);
  return (
    <div data-testid="er-price-chart" className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
          <XAxis dataKey="t" tickFormatter={fmtShort} tick={AX} minTickGap={40} />
          <YAxis domain={dom} tick={AX} width={70} tickFormatter={(v) => usd(v, v > 100 ? 0 : 4)} />
          <Tooltip {...TT} formatter={(v, n) => [typeof v === "number" ? usd(v, v > 100 ? 2 : 5) : v, { c: "Kapanış", ema_fast: "Hızlı EMA", ema_slow: "Yavaş EMA", m_long: "LONG giriş", m_short: "SHORT giriş", m_exit: "Çıkış" }[n] || n]} />
          <Line type="monotone" dataKey="c" stroke="#F1F5F9" dot={false} strokeWidth={1.2} isAnimationActive={false} />
          <Line type="monotone" dataKey="ema_fast" stroke="#F59E0B" dot={false} strokeWidth={1.2} isAnimationActive={false} connectNulls />
          <Line type="monotone" dataKey="ema_slow" stroke="#3B82F6" dot={false} strokeWidth={1.2} isAnimationActive={false} connectNulls />
          <Scatter dataKey="m_long" shape={<Marker type="long" />} isAnimationActive={false} />
          <Scatter dataKey="m_short" shape={<Marker type="short" />} isAnimationActive={false} />
          <Scatter dataKey="m_exit" shape={<Marker type="exit" />} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export const RsiChart = ({ series, levels }) => (
  <div data-testid="er-rsi-chart" className="h-44">
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
        <XAxis dataKey="t" tickFormatter={fmtShort} tick={AX} minTickGap={40} />
        <YAxis domain={[0, 100]} ticks={[0, 25, 35, 50, 65, 75, 100]} tick={AX} width={70} />
        <Tooltip {...TT} formatter={(v) => [Number(v).toFixed(2), "RSI"]} />
        {levels.map((l) => <ReferenceLine key={l.v} y={l.v} stroke={l.c} strokeDasharray="4 4" label={{ value: l.label, fill: l.c, fontSize: 10, position: "insideTopRight" }} />)}
        <Line type="monotone" dataKey="rsi" stroke="#A78BFA" dot={false} strokeWidth={1.3} isAnimationActive={false} connectNulls />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

export const EquityChart = ({ equity, start }) => (
  <div data-testid="er-equity-chart" className="h-52">
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={equity} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <defs><linearGradient id="eqFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient></defs>
        <CartesianGrid stroke={GRID} strokeDasharray="3 3" />
        <XAxis dataKey="t" tickFormatter={fmtShort} tick={AX} minTickGap={40} />
        <YAxis domain={["auto", "auto"]} tick={AX} width={70} tickFormatter={(v) => usd(v, 0)} />
        <Tooltip {...TT} formatter={(v, n) => [usd(v), n === "equity" ? "Özsermaye" : "Bakiye"]} />
        <ReferenceLine y={start} stroke="#64748B" strokeDasharray="4 4" />
        <Area type="monotone" dataKey="equity" stroke="#10B981" fill="url(#eqFill)" strokeWidth={1.4} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);
